import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useProximities } from '~/proximity/useProximities';
import { useBoundaryToolActiveBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { useFrontendStateLeftSidebarOpenedMapToolSelector } from '~/store/frontendState/leftSidebar/leftSidebar.selectors';
import {
  TargetLayeredState, useIsMapLayeredSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { useMapSettingsDirectionsRoutesSelector } from '~/store/mapSettings/directions/mapSettingsDirections.selectors';
import { useDrawingItemsSelector } from '~/store/mapSettings/drawing/mapSettingsDrawing.selectors';
import { useAreMapSettingsFilteringSettingsActive } from '~/store/mapSettings/filtering/mapSettingsFiltering.selectors';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '~/store/mapSettings/grouping/mapSettingsGrouping.selectors';
import { useMapSettingsHeatmapsSelector } from '~/store/mapSettings/heatmaps/mapSettingsHeatmaps.selectors';
import { useMapSettingsPlacesSearchesSelector } from '~/store/mapSettings/places/mapSettingsPlaces.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { userSettingsSetToolCollapse } from '~/store/userData/settings/userDataSettings.actionCreators';
import { useUserDataSettingsToolCollapseSelector } from '~/store/userData/settings/userDataSettings.selectors';

const COLLAPSIBLE_MAP_TOOLS = [
  MapTool.Grouping,
  MapTool.Filter,
  MapTool.Layering,
  MapTool.Routing,
  MapTool.Boundary,
  MapTool.Proximity,
  MapTool.HeatMap,
  MapTool.Drawing,
  MapTool.Places,
  MapTool.DataLayers,
];

/*
  * There is only 1 way to change the persisted state of the tool collapse:
    * 1. The user clicks on the collapse button
  * There are 3 ways to change collapsed state of already opened tool:
    * 1. It's number of items is reduced to 0
    * 2. The user clicks on the collapse button
    * 3. The user changes the active tool
  * Because of the above, we introduce a local state isToolCollapsedInternal to manage the already opened tool
    * Note that properly this should be in redux/saga but it would be a huge undertaking for not yet proven feature
    * Move it there if this feature will be expanded in the future
*/
export const useToolCollapseState = () => {
  const mapTool = useFrontendStateLeftSidebarOpenedMapToolSelector();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const clientId = useClientIdSelector();
  const isMobileScreen = useIsMobileScreenSelector();
  const isToolCollapsedUserSettings = useUserDataSettingsToolCollapseSelector(clientId, mapId, mapTool);
  const activeGroupingColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const activeBoundaryToolBoundaryTerritoryGroups = useBoundaryToolActiveBTGsSelector();
  const isMapLayered = useIsMapLayeredSelector(TargetLayeredState.Any);
  const placesSearches = useMapSettingsPlacesSearchesSelector();
  const drawingItems = useDrawingItemsSelector();
  const heatmaps = useMapSettingsHeatmapsSelector();
  const routes = useMapSettingsDirectionsRoutesSelector();
  const areFilterSettingsActive = useAreMapSettingsFilteringSettingsActive();
  const proximityList = useProximities();

  const isToolCollapsible = mapTool ? COLLAPSIBLE_MAP_TOOLS.includes(mapTool) : false;

  const hasToolActiveListings = useMemo(() => {
    switch (mapTool) {
      case MapTool.Grouping:
        return activeGroupingColumns.length > 0;
      case MapTool.Filter:
        return activeGroupingColumns.length > 0 || areFilterSettingsActive;
      case MapTool.Boundary:
        return activeBoundaryToolBoundaryTerritoryGroups.length > 0;
      case MapTool.Layering:
        return isMapLayered;
      case MapTool.Routing:
        return routes.length > 0;
      case MapTool.Proximity:
        return proximityList.length > 0;
      case MapTool.HeatMap:
        return heatmaps.length > 0;
      case MapTool.Drawing:
        return drawingItems.length > 0;
      case MapTool.Places:
        return placesSearches.length > 0;
      default:
        return false;
    }
  }, [activeGroupingColumns.length, activeBoundaryToolBoundaryTerritoryGroups.length, drawingItems.length, heatmaps.length,
    isMapLayered, mapTool, placesSearches.length, proximityList.length, routes.length,
    areFilterSettingsActive,
  ]);

  const setToolCollapse = useCallback((newValue: boolean) => {
    if (!clientId || !mapId || !isToolCollapsible || !mapTool) {
      return;
    }

    dispatch(userSettingsSetToolCollapse(clientId, mapId, mapTool, newValue));
  }, [clientId, dispatch, mapId, mapTool, isToolCollapsible]);

  const setToolCollapseRef = useRef(setToolCollapse);
  setToolCollapseRef.current = setToolCollapse;
  const canToolBeCollapsed = !isToolCollapsible || !hasToolActiveListings;

  useEffect(() => {
    if (canToolBeCollapsed) {
      setToolCollapseRef.current(false);
    }
  }, [canToolBeCollapsed]);

  return {
    isToolCollapsible,
    isToolCollapsibleDisabled: canToolBeCollapsed || isMobileScreen,
    isToolCollapsed: !!isToolCollapsedUserSettings && !canToolBeCollapsed && !isMobileScreen,
    setToolCollapse,
  };
};
